import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiCheckbox,
  MiIcon,
  MiIconButton,
  MiInputSelectBox,
  MiInputTextbox,
} from "@miplatform/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCheck, mdiClose, mdiLink } from "@mdi/js";
import { Box, Divider } from "@mui/material";

const OFFSET_FROM_OPTIONS = [
  { value: "EffectiveScheduledDate", label: "Order Scheduled Date" },
  { value: "CompleteDate", label: "Order Completed Date" },
];

const buildOffsetFieldSchema = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .regex(/^-?\d+$/, `${label} must be a whole number.`);

const dependencyCardOffsetSchema = z.object({
  days: buildOffsetFieldSchema("Day Offset"),
  hours: buildOffsetFieldSchema("Hour Offset"),
});

type DependencyCardOffsetFormValues = z.infer<
  typeof dependencyCardOffsetSchema
>;

interface IDependencyCardStyleProps {
  indentation: number | undefined;
  isAddingDependency: boolean;
  isDisable: boolean;
}

export interface IDependencyCard extends IDependencyCardStyleProps {
  itemId: string;
  title: string;
  canBeSelected: boolean;
  days: string;
  editDays: (v: number) => void;
  hours: string;
  isChecked: boolean;
  editHours: (v: number) => void;
  offsetTargetProperty?: string | null;
  editOffsetTargetProperty: (v: string) => void;
  onOffsetFormValidityChange: (itemId: string, isValid: boolean) => void;
  setAddingDependency: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  onCheck: () => void;
  labels: IDependencyCardLabel[];
  dependencyLabels: IDependencyCardLabel[];
}

export interface IDependencyCardLabel {
  dependencyLabel?: string | null;
  labelColor: string;
  color: string;
}

export const DependencyCard = ({
  itemId,
  title,
  indentation = 0,
  isAddingDependency = false,
  canBeSelected = false,
  isDisable,
  days,
  editDays,
  hours,
  editHours,
  offsetTargetProperty,
  editOffsetTargetProperty,
  onOffsetFormValidityChange,
  setAddingDependency,
  onCancel,
  onConfirm,
  onCheck,
  isChecked,
  labels = [],
  dependencyLabels = [],
}: IDependencyCard) => {
  const { classes, theme } = useStyles({
    indentation,
    isAddingDependency,
    isDisable,
  });

  const {
    control,
    reset,
    formState: { errors },
  } = useForm<DependencyCardOffsetFormValues>({
    mode: "onChange",
    defaultValues: {
      days,
      hours,
    },
    resolver: zodResolver(dependencyCardOffsetSchema),
  });

  useEffect(() => {
    reset({ days, hours });
  }, [days, hours, reset]);

  useEffect(() => {
    onOffsetFormValidityChange(itemId, !errors.days && !errors.hours);
  }, [errors.days, errors.hours, itemId, onOffsetFormValidityChange]);

  return (
    <Box className={classes.cardContainerBase}>
      {!isAddingDependency && canBeSelected && (
        <MiCheckbox
          handleChange={onCheck}
          value={isChecked}
          disabled={isDisable}
        />
      )}
      <Box className={classes.titleAndDepContainer}>
        <Box color={theme.palette.cyan} className={classes.titleContainer}>
          <Box className={classes.labelContainer}>
            {labels.map((x, index) => (
              <Chip
                key={`label-order-card-${title}-${index}`}
                className={classes.chip}
                label={`${x.dependencyLabel}.`}
                color={x.color}
                labelColor={x.labelColor}
              />
            ))}
          </Box>
          <Box className={classes.title}>{title}</Box>
        </Box>
        <Box className={classes.dependencyContainer}>
          {dependencyLabels.length > 0 && (
            <>
              <Box className={classes.dependencyTitle}>Depends On: </Box>
              {dependencyLabels.map((x, index) => (
                <Chip
                  key={`label-dependency-order-card-${title}-${index}`}
                  className={classes.dependencyChip}
                  label={x.dependencyLabel}
                  color={x.color}
                  labelColor={x.labelColor}
                  showBorder
                />
              ))}
            </>
          )}
        </Box>
      </Box>

      <Box className={classes.inputContainer}>
        <MiInputSelectBox
          label="Offset From"
          className={classes.offsetFromSelect}
          options={OFFSET_FROM_OPTIONS}
          value={offsetTargetProperty ?? "ApprovedStartDate"}
          handleChange={(e: { target: { value: string } }) =>
            editOffsetTargetProperty(e.target.value)
          }
          disabled={isAddingDependency || isDisable}
          disableNone
        />
        <Controller
          name="days"
          control={control}
          render={({ field }) => (
            <MiInputTextbox
              type="number"
              label="Day Offset"
              placeholder="Day Offset"
              className={classes.input}
              value={field.value}
              onBlur={field.onBlur}
              onChange={(e) => {
                const { value } = e.target;
                field.onChange(value);
                if (
                  buildOffsetFieldSchema("Day Offset").safeParse(value).success
                ) {
                  editDays(Number(value));
                }
              }}
              error={errors.days}
              inputProps={{ step: 1 }}
              disabled={isAddingDependency || isDisable}
            />
          )}
        />
        <Controller
          name="hours"
          control={control}
          render={({ field }) => (
            <MiInputTextbox
              type="number"
              label="Hour Offset"
              placeholder="Hour Offset"
              className={classes.input}
              value={field.value}
              onBlur={field.onBlur}
              onChange={(e) => {
                const { value } = e.target;
                field.onChange(value);
                if (
                  buildOffsetFieldSchema("Hour Offset").safeParse(value).success
                ) {
                  editHours(Number(value));
                }
              }}
              error={errors.hours}
              inputProps={{ step: 1 }}
              disabled={isAddingDependency || isDisable}
            />
          )}
        />
        <Divider
          style={{ height: "auto" }}
          orientation="vertical"
          variant="middle"
        />
        {!isAddingDependency && (
          <MiIconButton
            className={classes.button}
            disabled={isDisable}
            onClick={() => setAddingDependency()}
          >
            <MiIcon
              path={mdiLink}
              color={
                isDisable
                  ? theme.palette.grey.medium
                  : theme.palette.primary.main
              }
              size={0.7}
            />
          </MiIconButton>
        )}
        {isAddingDependency && (
          <>
            <MiIconButton className={classes.closeButton} onClick={onCancel}>
              <MiIcon
                path={mdiClose}
                color={theme.palette.red.contrastText}
                size={0.7}
              />
            </MiIconButton>
            <MiIconButton className={classes.saveButton} onClick={onConfirm}>
              <MiIcon
                path={mdiCheck}
                color={theme.palette.green.contrastText}
                size={0.7}
              />
            </MiIconButton>
          </>
        )}
      </Box>
    </Box>
  );
};

const Chip = ({
  color,
  label,
  labelColor,
  className,
  showBorder = false,
}: {
  label?: string | null | undefined;
  labelColor: string;
  color: string;
  showBorder?: boolean;
  className: string;
}) => {
  return (
    <Box
      style={{
        backgroundColor: color,
        color: labelColor,
        border: showBorder ? `1px solid ${labelColor}` : undefined,
      }}
      className={className}
    >
      {label}
    </Box>
  );
};

const useStyles = makeStyles<IDependencyCardStyleProps>()(
  (theme, { indentation, isAddingDependency, isDisable }) => {
    const opacity = isDisable ? "40%" : "100%";

    return {
      cardContainerBase: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        overflow: "hidden",
        backgroundColor: isAddingDependency
          ? theme.palette.primary.light
          : theme.palette.background.default,
        color: isDisable
          ? theme.palette.grey.medium
          : theme.palette.primary.main,
        border: `1px solid ${
          isAddingDependency
            ? theme.palette.primary.main
            : theme.palette.grey.lightAccent
        }`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        boxShadow: "0px 2px 2px 0px #0000001F",
        fontWeight: theme.fontWeight.semiBold,
        fontSize: theme.fontSize.small,
        marginLeft: theme.spacing(8 * (indentation ?? 0)),
      },
      titleAndDepContainer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      },
      labelContainer: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        spacing: theme.spacing(1),
      },
      titleContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "start",
        alignItems: "center",
        width: "auto",
      },
      chip: {
        padding: theme.spacing(1),
        fontWeight: theme.fontWeight.semiBold,
        margin: theme.spacing(0.5),
        borderRadius: theme.spacing(1),
        opacity,
      },
      title: {
        marginLeft: theme.spacing(1),
        opacity,
      },
      inputContainer: {
        display: "flex",
        flexDirection: "row",
      },
      input: {
        maxWidth: "120px",
        marginRight: theme.spacing(2),
        "& .MuiOutlinedInput-root": {
          backgroundColor: theme.palette.common.white,
        },
      },
      offsetFromSelect: {
        maxWidth: "200px",
        marginRight: theme.spacing(2),
        "& .MuiOutlinedInput-root": {
          backgroundColor: theme.palette.common.white,
        },
      },
      button: {
        marginLeft: theme.spacing(2),
        border: `1px solid ${
          isDisable ? theme.palette.grey.main : theme.palette.primary.dark
        }`,
        borderRadius: theme.spacing(1),
      },
      dependencyTitle: {
        fontSize: theme.fontSize.small,
        fontWeight: theme.fontWeight.regular,
        color: theme.palette.grey.main,
        opacity,
      },
      dependencyContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      },
      dependencyChip: {
        marginLeft: theme.spacing(1),
        fontSize: theme.fontSize.xsmall,
        fontWeight: theme.fontWeight.regular,
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(1),
        opacity,
      },
      closeButton: {
        backgroundColor: theme.palette.red.main,
        marginLeft: theme.spacing(2),
        borderRadius: theme.spacing(1),
        "&:hover": {
          backgroundColor: theme.palette.red.accent,
        },
      },
      saveButton: {
        backgroundColor: theme.palette.green.main,
        marginLeft: theme.spacing(2),
        borderRadius: theme.spacing(1),
        "&:hover": {
          backgroundColor: theme.palette.green.accent,
        },
      },
    };
  }
);
